import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { UserInfoInterface } from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { DetailIconComponent } from '@sharedModule/components/detail-icon/detail-icon.component';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { StatusPillPipe } from '@sharedModule/pipe/status-pill/status-pill.pipe';
import { FormService } from '@sharedModule/services/form/form.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { environment } from '@env/environment';
import { Subject, takeUntil } from 'rxjs';
import {
  ONLINE_TRANSACTION_STATUS,
  OFFLINE_TRANSACTION_STATUS,
  PAYOUT_STATUS,
  PAYOUT_STATUS_DEFAULT,
  PAYOUT_STATUS_PARTNER,
} from '../../constant/transaction-details.constant';
import { TransactionService } from '../../services/transaction.service';
import {
  TransactionListInterface,
  TransactionRecordInterface,
} from '../../types/transaction-list';

@Component({
  selector: 'app-transaction-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    InputModule,
    MaterialModule,
    HasAccessDirective,
    DetailIconComponent,
    StatusPillPipe,
  ],
  templateUrl: './transaction-details.component.html',
  styleUrl: './transaction-details.component.scss',
})
export class TransactionDetailsComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.transactions;
  public readonly onlineTransactionStatus = ONLINE_TRANSACTION_STATUS;
  public readonly offlineTransactionStatus = OFFLINE_TRANSACTION_STATUS;
  public readonly payoutStatus = PAYOUT_STATUS;
  transactionId!: string;
  transactionData?: TransactionListInterface;
  transactionRecord: TransactionRecordInterface[] = [];
  selectedStatus!: string;
  updateTxnStatusForm!: FormGroup;
  updatePayoutStatusForm!: FormGroup;
  canUpdatePayoutStatus = false;
  userInfo?: UserInfoInterface;
  isSuperAdmin!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formService: FormService,
    private partnerService: PartnerService,
    private transactionService: TransactionService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService
  ) {
    this.transactionId = this.route.snapshot.paramMap.get('id') ?? '';
    this.userInfo = this.authenticationService.getUserInfo();
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  get senderName(): string {
    return this.joinName(
      this.transactionData?.sender_first_name,
      this.transactionData?.sender_middle_name,
      this.transactionData?.sender_last_name
    );
  }

  get recipientName(): string {
    return this.joinName(
      this.transactionData?.beneficiary_first_name,
      this.transactionData?.beneficiary_middle_name,
      this.transactionData?.beneficiary_last_name
    );
  }

  /** Joins the name parts, dropping the middle name when it is absent. */
  private joinName(...parts: (string | undefined)[]): string {
    return parts.filter(Boolean).join(' ') || '—';
  }

  ngOnInit(): void {
    this.updateTxnStatusForm = new FormGroup({
      [this.attrConstant.transaction_status.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.amount_paid.key]: new FormControl(''),
      [this.attrConstant.reason.key]: new FormControl(''),
    });
    // Status is the only thing the user sets; the endpoint's amount and
    // message are filled in from the transaction at submit time.
    this.updatePayoutStatusForm = new FormGroup({
      [this.attrConstant.payout_status.key]: new FormControl(
        PAYOUT_STATUS_DEFAULT,
        [Validators.required]
      ),
    });
    this.loadData();
  }

  loadData(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    const payload = this.authenticationService.isSuperAdmin()
      ? {
          id: id.split('-')[0],
          partner_id: +id.split('-')[1],
        }
      : { id: id };
    this.transactionService
      .getTransactionDetails(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.transactionData = x.response;
            this.transactionRecord = this.toTransactionRecords(
              x.response.transaction_record
            );
            this.resolvePayoutStatusAccess(x.response);
          }
        },
      });
  }

  /**
   * getSenderTransaction returns transaction_record as an array, but it used to
   * send a JSON string, so both are accepted. The previous JSON.parse() on the
   * array stringified it to "[object Object]" and threw, which left the payment
   * history permanently empty for every transaction that had records.
   */
  private toTransactionRecords(
    record: TransactionListInterface['transaction_record']
  ): TransactionRecordInterface[] {
    if (!record) return [];
    if (Array.isArray(record)) return record;
    try {
      const parsed = JSON.parse(record);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  onTxnStatusChange(event: { value: string }): void {
    this.selectedStatus = event.value;
    const controlArray =
      event.value === 'Partial Payment'
        ? [
            { name: 'amount_paid', type: 'add' },
            { name: 'reason', type: 'clear' },
          ]
        : event.value === 'Reject'
          ? [
              { name: 'reason', type: 'add' },
              { name: 'amount_paid', type: 'clear' },
            ]
          : [
              { name: 'reason', type: 'clear' },
              { name: 'amount_paid', type: 'clear' },
            ];
    this.upsertValidators(controlArray);
  }

  upsertValidators(controlArray: { name: string; type: string }[]): void {
    for (const data of controlArray) {
      if (data.type === 'add') {
        this.updateTxnStatusForm.controls[data.name].addValidators(
          Validators.required
        );
        this.formService.enableControls(this.updateTxnStatusForm, [data.name]);
      } else {
        this.updateTxnStatusForm.controls[data.name].clearValidators();
        this.formService.disableControls(this.updateTxnStatusForm, [data.name]);
      }
      this.updateTxnStatusForm.controls[data.name].updateValueAndValidity();
    }
  }

  onTxnStatusUpdate(): void {
    if (this.updateTxnStatusForm.valid) {
      const payload =
        this.selectedStatus === 'Paid'
          ? {
              amount_paid: this.transactionData!.remaining_amount,
              ...this.updateTxnStatusForm.value,
            }
          : this.updateTxnStatusForm.value;
      this.transactionService
        .updateTransactionStatus({
          ...payload,
          trans_session_id: this.transactionId,
          pay_in_status:
            this.selectedStatus === 'Reject' ? 'Failed' : 'Successful',
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.router.navigate(['/transaction/list']);
            }
          },
        });
    }
  }

  /**
   * Decides whether this transaction may have its pay-out status updated.
   *
   * The feature belongs to one partner. That partner's own build is its whole
   * portal, so every transaction in it qualifies. The platform-admin build
   * serves every partner, so there the transaction itself has to belong to
   * them - resolved by matching the partner's name in the dropdown the auth
   * guard already loads, rather than hardcoding its id.
   */
  private resolvePayoutStatusAccess(
    transaction: TransactionListInterface
  ): void {
    if (environment.partnerName === PAYOUT_STATUS_PARTNER) {
      this.canUpdatePayoutStatus = true;
      return;
    }
    if (!this.isSuperAdmin) return;
    this.partnerService
      .getPartnerListDropdown()
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: partners => {
          this.canUpdatePayoutStatus = partners.some(
            partner =>
              partner.name === PAYOUT_STATUS_PARTNER &&
              partner.partner_id === transaction.partner_id
          );
        },
      });
  }

  onPayoutStatusUpdate(): void {
    if (!this.updatePayoutStatusForm.valid || !this.transactionData) return;
    this.transactionService
      .updatePayoutStatus({
        ...this.updatePayoutStatusForm.value,
        // The endpoint still takes these, but they are not the user's to set
        // here - the pay-out is for the full destination amount.
        amount: this.transactionData.destination_amount,
        message: '',
        // Read off the record rather than the route param, which carries the
        // partner id appended for the platform admin.
        trans_session_id: this.transactionData.trans_session_id,
        ...(this.isSuperAdmin
          ? { partner_id: this.transactionData.partner_id }
          : {}),
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/transaction/list']);
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
